import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

export interface JsonToCSharpState {
  inputJson: string;
  generatedCSharp: string;
  isConverting: boolean;
  error: string | null;
  className: string;
  useProperties: boolean;
  useNullableReferenceTypes: boolean;
  usePascalCase: boolean;
}

const initialState: JsonToCSharpState = {
  inputJson: '',
  generatedCSharp: '',
  isConverting: false,
  error: null,
  className: 'MyClass',
  useProperties: true,
  useNullableReferenceTypes: true,
  usePascalCase: true,
};

export const convertJsonToCSharpAsync = createAsyncThunk(
  'jsonToCSharp/convertJsonToCSharpAsync',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { jsonToCSharp: JsonToCSharpState };
      const { inputJson, className, useProperties, useNullableReferenceTypes, usePascalCase } = state.jsonToCSharp;

      if (!inputJson.trim()) {
        throw new Error('Please enter JSON data to convert');
      }

      let jsonData;
      try {
        jsonData = JSON.parse(inputJson);
      } catch (parseError) {
        throw new Error('Invalid JSON format. Please check your input.');
      }

      // Note: Analytics service would be injected here in a real implementation
      // For now, we'll just log the event
      console.log('JSON to C# Converter: convert event', 'json_to_csharp');

      const cSharpCode = generateCSharpClasses(jsonData, className, useProperties, useNullableReferenceTypes, usePascalCase);
      
      return { cSharpCode };
    } catch (error) {
      // Note: Analytics service would be injected here in a real implementation
      // For now, we'll just log the event
      console.log('JSON to C# Converter: error event', 'conversion_failed');
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to convert JSON to C#');
    }
  }
);

// Helper function to generate C# classes from JSON
function generateCSharpClasses(
  jsonData: any, 
  className: string, 
  useProperties: boolean, 
  useNullableReferenceTypes: boolean,
  usePascalCase: boolean
): string {
  const classes = new Map<string, string>();
  const processedTypes = new Set<string>();
  
  function generateClass(obj: any, name: string, isRoot: boolean = false): string {
    if (processedTypes.has(name)) {
      return '';
    }
    processedTypes.add(name);
    
    let classCode = '';
    
    // Class declaration
    classCode += `public class ${name}\n{\n`;
    
    // Generate properties for each field
    for (const [key, value] of Object.entries(obj)) {
      const propertyName = usePascalCase ? toPascalCase(key) : key;
      const propertyType = getCSharpType(value, key, useNullableReferenceTypes);
      
      // Property declaration
      if (useProperties) {
        classCode += `    public ${propertyType} ${propertyName} { get; set; }\n`;
      } else {
        classCode += `    public ${propertyType} ${propertyName};\n`;
      }
      
      // Generate nested classes for complex objects
      if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        const nestedClassName = toPascalCase(key);
        const nestedClass = generateClass(value, nestedClassName);
        if (nestedClass) {
          classes.set(nestedClassName, nestedClass);
        }
      }
      
      // Generate classes for array items if they are objects
      if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
        const arrayItemClassName = toPascalCase(key.slice(0, -1)); // Remove 's' from plural
        const arrayItemClass = generateClass(value[0], arrayItemClassName);
        if (arrayItemClass) {
          classes.set(arrayItemClassName, arrayItemClass);
        }
      }
    }
    
    classCode += '}\n';
    return classCode;
  }
  
  // Generate root class
  const rootClass = generateClass(jsonData, className, true);
  classes.set(className, rootClass);
  
  // Combine all classes
  let result = '';
  classes.forEach((classCode) => {
    result += classCode + '\n';
  });
  
  return result.trim();
}

function getCSharpType(value: any, originalKey: string, useNullableReferenceTypes: boolean): string {
  if (value === null) {
    return useNullableReferenceTypes ? 'string?' : 'string';
  }
  
  switch (typeof value) {
    case 'string':
      return useNullableReferenceTypes ? 'string?' : 'string';
    case 'number':
      if (Number.isInteger(value)) {
        return 'int';
      }
      return 'double';
    case 'boolean':
      return 'bool';
    case 'object':
      if (Array.isArray(value)) {
        if (value.length === 0) {
          return 'List<object>';
        }
        const itemType = getCSharpType(value[0], originalKey, useNullableReferenceTypes);
        return `List<${itemType}>`;
      }
      return toPascalCase(originalKey);
    default:
      return 'object';
  }
}

function toPascalCase(str: string): string {
  // Handle camelCase by inserting space before capital letters
  const withSpaces = str.replace(/([a-z])([A-Z])/g, '$1 $2');
  
  return withSpaces
    .replace(/[^a-zA-Z0-9]/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('')
    .replace(/[^a-zA-Z0-9]/g, '');
}

const jsonToCSharpSlice = createSlice({
  name: 'jsonToCSharp',
  initialState,
  reducers: {
    inputJsonChanged: (state, action: PayloadAction<string>) => {
      state.inputJson = action.payload;
      state.error = null;
    },
    classNameChanged: (state, action: PayloadAction<string>) => {
      state.className = action.payload;
    },
    propertiesToggled: (state, action: PayloadAction<boolean>) => {
      state.useProperties = action.payload;
    },
    nullableReferenceTypesToggled: (state, action: PayloadAction<boolean>) => {
      state.useNullableReferenceTypes = action.payload;
    },
    pascalCaseToggled: (state, action: PayloadAction<boolean>) => {
      state.usePascalCase = action.payload;
    },
    clearGenerated: (state) => {
      state.generatedCSharp = '';
      state.error = null;
    },
    clearAll: (state) => {
      state.inputJson = '';
      state.generatedCSharp = '';
      state.error = null;
      state.className = 'MyClass';
      state.useProperties = true;
      state.useNullableReferenceTypes = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(convertJsonToCSharpAsync.pending, (state) => {
        state.isConverting = true;
        state.error = null;
      })
      .addCase(convertJsonToCSharpAsync.fulfilled, (state, action) => {
        state.isConverting = false;
        state.generatedCSharp = action.payload.cSharpCode;
        state.error = null;
      })
      .addCase(convertJsonToCSharpAsync.rejected, (state, action) => {
        state.isConverting = false;
        state.error = action.payload as string;
        state.generatedCSharp = '';
      });
  },
});

export const { 
  inputJsonChanged, 
  classNameChanged, 
  propertiesToggled, 
  nullableReferenceTypesToggled,
  pascalCaseToggled,
  clearGenerated, 
  clearAll 
} = jsonToCSharpSlice.actions;

// Selectors
export const selectState = (state: { jsonToCSharp: JsonToCSharpState }) => state.jsonToCSharp;

export default jsonToCSharpSlice.reducer;
